// What the signed-in person may do in CRM.
//
// The backend enforces staff permissions (middleware/staffPermissions.js):
// CRM 'none' refuses every /api/leads, /api/contacts, /api/lead-analytics and
// /api/lead-statuses call with 403 PERMISSION_DENIED; 'view' refuses every
// write. The level comes from the shared GET /api/staff/me/permissions
// (useMyPermissions.js); whether the person is an org admin (owner or staff
// role admin, who alone may run the bulk deletes) from GET
// /api/contacts/bulk/me, asked only when CRM can be read at all. Anything
// that can't be read counts as 'edit', so a failed check never hides a
// working button; the global PermissionNotice toast still explains a refusal.
import { useEffect, useState } from "react";
import { fetchApi } from "../marketing/access";
import { levelOf, loadMyPermissions, resetMyPermissions } from "../useMyPermissions";

const TTL = 60 * 1000; // same as the permissions cache
let cached = null; // { token, at, promise }

async function readAccess() {
  const level = levelOf(await loadMyPermissions(), "CRM");
  if (level === "none") return { level, isOrgAdmin: false };
  const me = await fetchApi("/api/contacts/bulk/me");
  const isOrgAdmin = me.ok ? !!me.data?.data?.is_org_admin : null; // null = couldn't tell
  return { level, isOrgAdmin };
}

export function loadCrmAccess() {
  const token = localStorage.getItem("token");
  if (!cached || cached.token !== token || Date.now() - cached.at > TTL) {
    cached = { token, at: Date.now(), promise: readAccess().catch(() => ({ level: "edit", isOrgAdmin: null })) };
  }
  return cached.promise;
}

/** Forget the cached answer (after a 403 says it changed). */
export const resetCrmAccess = () => { cached = null; resetMyPermissions(); };

/**
 * { loading, canEdit, viewOnly, noAccess, isOrgAdmin }
 * isOrgAdmin is true / false, or null when it couldn't be read (show the
 * admin-only action; the backend's refusal is shown if it isn't allowed).
 * While loading everything stays enabled, so nobody sees buttons flicker off.
 */
export function useCrmAccess() {
  const [state, setState] = useState({ loading: true, level: "edit", isOrgAdmin: null });
  useEffect(() => {
    let live = true;
    loadCrmAccess().then((a) => live && setState({ loading: false, level: a.level, isOrgAdmin: a.isOrgAdmin }));
    return () => { live = false; };
  }, []);
  return {
    loading: state.loading,
    canEdit: state.level === "edit",
    viewOnly: state.level === "view",
    noAccess: state.level === "none",
    isOrgAdmin: state.isOrgAdmin,
  };
}

/** Tooltip for a control a view-only person can't use. */
export const VIEW_ONLY_TITLE = "View only. Ask an admin for edit access to CRM.";

/** A quiet line under a header: "You can view CRM but not change it." */
export function ViewOnlyNote({ className = "" }) {
  return <p role="note" className={`rounded-md bg-mist px-4 py-2.5 text-small text-muted ${className}`}>View only: you can see CRM but not change it. Ask an admin for edit access.</p>;
}
